import React from 'react';
import { Paper } from '@mui/material';
import PropTypes from 'prop-types';
import { Box, Typography } from '../atoms';

/**
 * Section Molecule
 * A section container with title and content area
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Section content
 * @param {string} props.title - Section title
 * @param {string} props.subtitle - Section subtitle
 * @param {React.ReactNode} props.titleIcon - Icon to display before the title
 * @param {React.ReactNode} props.actions - Actions to display in the header
 * @param {number} props.elevation - Paper elevation level
 * @param {string} props.variant - Paper variant (elevation, outlined)
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} Section component
 */
const Section = ({
  children,
  title,
  subtitle,
  titleIcon,
  actions,
  elevation = 0,
  variant = 'elevation',
  sx = {},
  ...rest
}) => {
  return (
    <Paper 
      elevation={elevation} 
      variant={variant}
      sx={{ 
        p: 0,
        // Removed default padding to allow specific instance overrides
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 'none',
        borderRadius: 0,
        bgcolor: 'white', // Ensure white background
        ...sx 
      }}
      {...rest}
    >
      {/* Section Header */}
      {(title || actions) && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 0, // Remove bottom margin
            pb: 0,
            borderBottom: 'none', // Remove border bottom
            margin: 0
          }}
        >
          {/* Title with optional icon */}
          {title && (
            <Box sx={{ mb: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
                {titleIcon && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                    {titleIcon}
                  </Box>
                )}
                <Typography variant="h6" component="h2" fontWeight="medium" sx={{ mb: 0, margin: 0 }}>
                  {title}
                </Typography>
              </Box>
              
              {/* Subtitle */}
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
          
          {/* Actions */}
          {actions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {actions}
            </Box>
          )}
        </Box>
      )}
      
      {/* Section Content */}
      <Box sx={{
        flexGrow: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      className="section-component">
        {children}
      </Box>
    </Paper>
  );
};

Section.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  titleIcon: PropTypes.node,
  actions: PropTypes.node,
  elevation: PropTypes.number,
  variant: PropTypes.oneOf(['elevation', 'outlined']),
  sx: PropTypes.object
};

export default Section;